import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    getBluedartInternalUsers,
    updateBluedartInternalUserScope,
    deactivateBluedartInternalUser,
    getBluedartScopeOptions,
} from '../../store/actions/staffActionCreator';
import { showMessage } from '../../utils/helper';
import { getPermissions } from '../../utils/permissions';
import { isBlueDartSuperAdmin } from '../../utils/appHelper';
import useUserManagementStyles from './useUserManagementStyles';
import { roleLabel } from './user.helper';

const DEFAULT_PAGE_SIZE = 20;

const useUserManagement = () => {
    const dispatch = useDispatch();
    const styles = useUserManagementStyles();

    const [users, setUsers] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [loading, setLoading] = useState(false);

    const [searchTextValue, setSearchTextValue] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [scopeOptions, setScopeOptions] = useState<any>({
        zones: [],
        stores: [],
        assignableRoles: [],
        vendorOrgs: [],
    });

    const [scopeModalUser, setScopeModalUser] = useState<any>(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [confirmDeactivate, setConfirmDeactivate] = useState<any>(null);

    const permissions = useMemo(() => getPermissions('user_management'), []);

    const fetchUsers = useCallback(() => {
        setLoading(true);
        dispatch(
            getBluedartInternalUsers(
                {
                    searchText: appliedSearch,
                    pageNo,
                    pageSize,
                },
                (response: any) => {
                    setLoading(false);
                    const body = response?.response ?? {};
                    setUsers(body.users ?? []);
                    setTotalCount(body.totalCount ?? 0);
                },
                (error: any) => {
                    setLoading(false);
                    setUsers([]);
                    setTotalCount(0);
                    showMessage(
                        error?.status?.message ?? 'Could not load users',
                        'error',
                    );
                },
            ),
        );
    }, [dispatch, appliedSearch, pageNo, pageSize]);

    // Scope options drive both the create form and the edit modal, and are already narrowed
    // server-side to what this caller may grant — so they are fetched once, not per modal open.
    const fetchScopeOptions = useCallback(() => {
        dispatch(
            getBluedartScopeOptions(
                (response: any) => setScopeOptions(response?.response ?? {}),
                () =>
                    setScopeOptions({
                        zones: [],
                        stores: [],
                        assignableRoles: [],
                        vendorOrgs: [],
                    }),
            ),
        );
    }, [dispatch]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchScopeOptions();
    }, [fetchScopeOptions]);

    const handleChangePage = (_event: unknown, newPage: number) =>
        setPageNo(newPage);

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const handleSearchSubmit = () => {
        setPageNo(0);
        setAppliedSearch(searchTextValue.trim());
    };

    const handleSearchClear = () => {
        setSearchTextValue('');
        setAppliedSearch('');
        setPageNo(0);
    };

    const handleScopeSave = (
        userId: string | number,
        addScopeValues: string[],
        removeScopeValues: string[],
    ) => {
        dispatch(
            updateBluedartInternalUserScope(
                userId,
                { addScopeValues, removeScopeValues },
                () => {
                    showMessage('Access updated', 'success');
                    setScopeModalUser(null);
                    fetchUsers();
                },
                (error: any) => {
                    // The backend blocks removing the last store and reports out-of-zone values
                    // by message; surface it verbatim rather than a generic failure.
                    showMessage(
                        error?.status?.message ?? 'Could not update access',
                        'error',
                    );
                },
            ),
        );
    };

    const handleConfirmDeactivate = () => {
        if (!confirmDeactivate) return;
        dispatch(
            deactivateBluedartInternalUser(
                confirmDeactivate.id,
                () => {
                    showMessage('User deactivated', 'success');
                    setConfirmDeactivate(null);
                    fetchUsers();
                },
                (error: any) => {
                    setConfirmDeactivate(null);
                    showMessage(
                        error?.status?.message ?? 'Could not deactivate user',
                        'error',
                    );
                },
            ),
        );
    };

    /** Role is fixed at creation in v1 (UM-8), so the row menu offers scope and deactivate only.
     * Neither a Super Admin (global) nor a vendor login (bounded by its organisation) has any
     * scope rows to edit. */
    const canEditScope = (row: any) =>
        !!permissions.update &&
        row?.scopeType !== 'GLOBAL' &&
        row?.scopeType !== 'ORG';

    const canDeactivate = (row: any) => !!permissions.delete;

    const stores = scopeOptions?.stores ?? [];
    const zones = scopeOptions?.zones ?? [];

    return {
        users,
        totalCount,
        pageNo,
        pageSize,
        loading,
        styles,
        permissions,
        searchTextValue,
        setSearchTextValue,
        appliedSearch,
        handleChangePage,
        handleChangeRowsPerPage,
        handleSearchSubmit,
        handleSearchClear,
        scopeModalUser,
        setScopeModalUser,
        handleScopeSave,
        confirmDeactivate,
        setConfirmDeactivate,
        handleConfirmDeactivate,
        createOpen,
        setCreateOpen,
        stores,
        zones,
        assignableRoles: scopeOptions?.assignableRoles ?? [],
        vendorOrgs: scopeOptions?.vendorOrgs ?? [],
        canEditScope,
        canDeactivate,
        refetch: fetchUsers,
        roleLabel,
        isSuperAdmin: isBlueDartSuperAdmin(),
    };
};

export default useUserManagement;
